import FormLabel from "@/components/common/FormLabel";
import ImageUploadField from "@/components/common/ImageUploadField";
import SectionDivider from "@/components/common/SectionDivider";
import { SearchBar } from "@/components/SearchBox/SearchBar";
import { ValidateInput } from "@/components/SearchBox/ValidateInput";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import { validateEmail, validateUsername } from "@/utils/validators";

import {
  Loader2,
  Plus,
  X,
  Building2,
  User,
  Lock,
  Mail,
  Shield,
  Upload,
} from "lucide-react";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";

/* ─────────────────────────────────────────────
   STYLE TOKENS
───────────────────────────────────────────── */

const inputBase =
  "w-full px-3 py-2 bg-white dark:bg-slate-800 border rounded-lg text-sm " +
  "text-slate-900 dark:text-white placeholder-slate-400 " +
  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
const labelBase =
  "block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
const selectBase =
  "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 " +
  "rounded-lg text-sm text-slate-900 dark:text-white appearance-none cursor-pointer " +
  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
const sectionCard =
  "p-5 rounded-2xl border border-slate-800 bg-white dark:bg-slate-900/70 backdrop-blur-sm";

/* ─────────────────────────────────────────────
   MODAL
───────────────────────────────────────────── */

const AddUserDialog = ({
  open,
  onOpenChange,
  modalError,
  profileImageFile,
  setProfileImageFile,
  handleCreateUser,
  userForm,
  setUserForm,
  companies,
  roles,
  modalLoading,
}) => {
  const { officeTypeList } = useSelector((state) => state.common);
  const [profileImage, setProfileImage] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setUserForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
            [&>button]:hidden

            p-0
            overflow-hidden

            w-[95vw]
            sm:max-w-5xl

            h-[92vh]

            flex flex-col

            border border-slate-800
            bg:white dark:bg-slate-900
            text-white

            rounded-3xl
            shadow-2xl
        "
      >
        {/* ───────────────── HEADER ───────────────── */}

        <div
          className="
                shrink-0

                flex items-center justify-between

                px-6 py-5

                border-b border-slate-700 bg-gradient-to-r from-slate-600 to-slate-700
            "
        >
          <div className="flex items-center gap-3">
            <div
              className="
                        w-11 h-11 rounded-2xl

                        bg-gradient-to-br
                        from-teal-500
                        to-emerald-600

                        flex items-center justify-center

                        shadow-lg shadow-teal-500/20
                    "
            >
              <Plus className="w-5 h-5 text-white" />
            </div>

            <div>
              <h2 className="text-lg font-bold text-white">Add New User</h2>

              <p className="text-xs text-slate-400 mt-0.5">
                Create and manage user account access
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="
                    w-9 h-9 rounded-xl

                    flex items-center justify-center

                    text-slate-400
                    hover:text-white
                    hover:bg-slate-800

                    transition-all
                "
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ───────────────── ERROR ───────────────── */}

        {modalError && (
          <div
            className="
                    mx-6 mt-5
                    p-4 rounded-2xl

                    border border-red-500/20
                    bg-red-500/10

                    text-red-400 text-sm
                "
          >
            {modalError}
          </div>
        )}

        {/* ───────────────── FORM ───────────────── */}

        <form
          id="add-user-form"
          onSubmit={handleCreateUser}
          className="flex flex-1 flex-col overflow-hidden"
        >
          {/* ───────────────── BODY ───────────────── */}

          <div
            className="
                    flex-1
                    overflow-y-auto

                    px-6 py-5

                    space-y-5

                    scrollbar-thin
                    scrollbar-thumb-slate-700
                    scrollbar-track-transparent
                "
          >
            {/* ───────── IDENTITY SECTION ───────── */}

            <div className={sectionCard}>
              <SectionDivider icon={User} title="Identity" color="blue" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {/* First Name */}

                <div>
                  <FormLabel required>First Name</FormLabel>

                  <input
                    type="text"
                    name="first_name"
                    value={userForm.first_name}
                    onChange={handleInputChange}
                    placeholder="John"
                    className={inputBase}
                    required
                  />
                </div>

                {/* Last Name */}

                <div>
                  <FormLabel required>Last Name</FormLabel>
                  <input
                    type="text"
                    name="last_name"
                    value={userForm.last_name}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    className={inputBase}
                    required
                  />
                </div>

                {/* Email */}

                <div>
                  <FormLabel required>Email Address</FormLabel>
                  <ValidateInput
                    type="email"
                    value={userForm.email}
                    onChange={(value) =>
                      setUserForm((prev) => ({
                        ...prev,
                        email: value,
                      }))
                    }
                    placeholder="john@company.com"
                    minLength={5}
                    debounceTime={600}
                    validator={validateEmail}
                    inputClassName={`${inputBase}`}
                  />
                </div>

                {/* Contact */}

                <div>
                  <FormLabel>Contact Number</FormLabel>

                  <input
                    type="text"
                    name="contact_no"
                    value={userForm.contact_no}
                    onChange={handleInputChange}
                    placeholder="+91 9876543210"
                    className={inputBase}
                  />
                </div>
                <div className="sm:col-span-2">
                  <ImageUploadField
                    label="Profile Image"
                    value={profileImage || userForm?.PROFILE_IMAGE || null}
                    maxSize={5}
                    accept={[
                      "image/png",
                      "image/jpeg",
                      "image/jpg",
                      "image/webp",
                    ]}
                    hint="PNG, JPG, WEBP • Max 5MB"
                    onChange={(previewUrl, file) => {
                      setProfileImage(previewUrl);

                      setProfileImageFile(file || null);

                      handleInputChange({
                        target: {
                          name: "PROFILE_IMAGE",
                          value: file || null,
                        },
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ───────── CREDENTIALS SECTION ───────── */}

            <div className={sectionCard}>
              <SectionDivider icon={Lock} title="Credentials" color="purple" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {/* Username */}

                <div>
                  <FormLabel required>Username</FormLabel>
                  <ValidateInput
                    value={userForm.username}
                    onChange={(value) =>
                      setUserForm((prev) => ({
                        ...prev,
                        username: value,
                      }))
                    }
                    placeholder="john_doe"
                    minLength={5}
                    debounceTime={600}
                    validator={validateUsername}
                    inputClassName={inputBase}
                    autoComplete="off"
                  />
                </div>

                {/* Password */}

                <div>
                  <FormLabel required>Password</FormLabel>
                  <div className="relative">
                    <Lock
                      className="
                            absolute left-3 top-1/2
                            -translate-y-1/2
                            w-4 h-4 text-slate-500
                        "
                    />
                    <input
                      type="password"
                      name="password"
                      value={userForm.password}
                      onChange={handleInputChange}
                      placeholder="Minimum 8 characters"
                      className={`${inputBase} pl-10`}
                      required
                      minLength={8}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ───────── ORGANISATION SECTION ───────── */}

            <div className={sectionCard}>
              <SectionDivider
                icon={Building2}
                title="Organisation"
                color="green"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {/* Company */}

                <div>
                  <FormLabel required>Company</FormLabel>

                  <SearchBar
                    searchAPI="v1/AutoSuggestion/GetCompanyAutoSuggestion?CompanyName="
                    selectReacord={(d) => {
                      handleInputChange({
                        target: {
                          name: "company_id",
                          value: d?.[0] || 0,
                        },
                      });

                      handleInputChange({
                        target: {
                          name: "company_name",
                          value: d?.[1] || "",
                        },
                      });
                    }}
                    value={userForm?.company_name || ""}
                    onchange={(val) => {
                      if (!val) {
                        handleInputChange({
                          target: { name: "company_id", value: 0 },
                        });

                        handleInputChange({
                          target: { name: "company_name", value: "" },
                        });
                      }
                    }}
                    minword={2}
                    showResults={1}
                  />
                </div>

                {/* Role */}

                <div>
                  <FormLabel required>Role</FormLabel>

                  <select
                    name="role_id"
                    value={userForm?.role_id}
                    onChange={handleInputChange}
                    className={selectBase}
                  >
                    <option value="" disabled hidden>
                      Select Role
                    </option>
                    {roles.map((r) => (
                      <option key={r.roleId} value={r.roleId}>
                        {r.roleName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Office Type */}

                <div>
                  <FormLabel required>Office Type</FormLabel>
                  <select
                    name="office_id"
                    value={userForm?.office_id}
                    onChange={handleInputChange}
                    className={selectBase}
                  >
                    <option value="">Select Office</option>

                    {officeTypeList?.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.officeType}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Profit Center */}

                <div>
                  <FormLabel>Profit Center</FormLabel>

                  <input
                    type="text"
                    name="profit_center"
                    value={userForm.profit_center}
                    onChange={handleInputChange}
                    placeholder="CORP-01"
                    className={inputBase}
                  />
                </div>

                {/* Profit Type */}

                <div>
                  <FormLabel>Profit Type</FormLabel>

                  <select
                    name="profit_type"
                    value={userForm.profit_type}
                    onChange={handleInputChange}
                    className={selectBase}
                  >
                    <option value="">Select Type</option>

                    <option value="FIXED">Fixed</option>

                    <option value="PERCENTAGE">Percentage</option>
                  </select>
                </div>

                {/* Profit */}

                <div>
                  <FormLabel>Profit Amount</FormLabel>

                  <input
                    type="number"
                    step="0.01"
                    name="profit"
                    value={userForm.profit}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className={inputBase}
                  />
                </div>
              </div>
            </div>

            {/* ───────── PERMISSIONS SECTION ───────── */}

            <div className={sectionCard}>
              <SectionDivider
                icon={Shield}
                title="Permissions & Status"
                color="amber"
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                {[
                  {
                    name: "salesperson",
                    label: "Is Sales Person",
                  },
                  {
                    name: "is_active",
                    label: "Is Active",
                  },
                  { name: "IS_FILEMANAGER", label: "File Manager" },
                ].map((item) => (
                  <label
                    key={item.name}
                    className="flex items-center gap-2.5 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 transition-colors group"
                  >
                    <div className="relative shrink-0">
                      <input
                        type="checkbox"
                        name={item.name}
                        checked={userForm?.[item.name] ?? false}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 rounded-full bg-slate-200 dark:bg-slate-600 peer-checked:bg-blue-500 transition-colors" />
                      <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
                    </div>

                    <span className="text-xs font-semibold text-slate-900 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                      {item.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Required Text */}

            <p className="text-[11px] text-slate-500 dark:text-slate-400 pb-24">
              <span className="text-red-500">*</span> Required fields
            </p>
          </div>
          <div className="shrink-0 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-end gap-3">
            {/* Cancel */}

            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="
                        h-10 px-5 rounded-xl

                        border border-slate-700

                        bg-slate-800
                        hover:bg-slate-700
                        hover:border-slate-600

                        text-sm font-medium text-slate-200

                        transition-all duration-200
                    "
            >
              Cancel
            </button>

            {/* Submit */}

            <button
              type="submit"
              disabled={modalLoading}
              className="
                        h-10 px-5 rounded-xl

                        bg-gradient-to-r
                        from-teal-500
                        to-emerald-500

                        hover:from-teal-600
                        hover:to-emerald-600

                        shadow-lg shadow-teal-500/20

                        text-sm font-semibold text-white

                        flex items-center gap-2

                        transition-all duration-200

                        disabled:opacity-50
                        disabled:cursor-not-allowed
                    "
            >
              {modalLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create User
                </>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserDialog;
